import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { useAuth } from "../Login/AuthContext";

const CarritoContext = createContext();

export const CarritoProvider = ({ children }) => {
  const { token, usuario, cargando } = useAuth();

  const [carrito, setCarrito] = useState(null);
  const [carritoItems, setCarritoItems] = useState([]);
  const [loadingCarrito, setLoadingCarrito] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL;

  const authHeaders = token
    ? { Authorization: `Bearer ${token}` }
    : {};

  const cargarCarrito = useCallback(async () => {
    if (!token || !usuario || usuario.rol !== "cliente") {
      setCarrito(null);
      setCarritoItems([]);
      return;
    }

    try {
      setLoadingCarrito(true);

      const response = await axios.get(`${API_URL}/carrito`, {
        headers: authHeaders,
      });

      const carritoBackend = response.data;
      setCarrito(carritoBackend);
      setCarritoItems(carritoBackend.items || []);
    } catch (error) {
      console.error("Error al cargar carrito:", error);
      setCarrito(null);
      setCarritoItems([]);
    } finally {
      setLoadingCarrito(false);
    }
  }, [API_URL, token, usuario]);

  useEffect(() => {
    if (!cargando) {
      cargarCarrito();
    }
  }, [cargando, cargarCarrito]);

  const agregarAlCarrito = async (producto, cantidad = 1) => {
    if (!token || !usuario || usuario.rol !== "cliente") {
      Swal.fire({
        icon: "info",
        title: "Iniciá sesión",
        text: "Necesitás iniciar sesión como cliente para usar el carrito.",
      });
      return false;
    }

    try {
      await axios.post(
        `${API_URL}/carrito/agregar`,
        {
          producto_id: producto.id,
          cantidad,
        },
        {
          headers: {
            "Content-Type": "application/json",
            ...authHeaders,
          },
        }
      );

      await cargarCarrito();
      return true;
    } catch (error) {
      const msg =
        error.response?.data?.message ||
        error.response?.data?.msg ||
        error.response?.data?.error ||
        "No se pudo agregar el producto al carrito.";

      Swal.fire({
        icon: "error",
        title: "Error",
        text: msg,
      });
      return false;
    }
  };

  const actualizarCantidad = async (itemId, cantidad) => {
    try {
      await axios.put(
        `${API_URL}/carrito/item/${itemId}`,
        { cantidad },
        {
          headers: {
            "Content-Type": "application/json",
            ...authHeaders,
          },
        }
      );

      await cargarCarrito();
    } catch (error) {
      const msg =
        error.response?.data?.message ||
        error.response?.data?.msg ||
        error.response?.data?.error ||
        "No se pudo actualizar la cantidad.";

      Swal.fire({
        icon: "error",
        title: "Error",
        text: msg,
      });
    }
  };

  const eliminarDelCarrito = async (itemId) => {
    try {
      await axios.delete(`${API_URL}/carrito/item/${itemId}`, {
        headers: authHeaders,
      });

      await cargarCarrito();
    } catch (error) {
      const msg =
        error.response?.data?.message ||
        error.response?.data?.msg ||
        error.response?.data?.error ||
        "No se pudo eliminar el producto.";

      Swal.fire({
        icon: "error",
        title: "Error",
        text: msg,
      });
    }
  };

  const vaciarCarrito = async () => {
    try {
      await axios.delete(`${API_URL}/carrito/vaciar`, {
        headers: authHeaders,
      });

      await cargarCarrito();
    } catch (error) {
      const msg =
        error.response?.data?.message ||
        error.response?.data?.msg ||
        error.response?.data?.error ||
        "No se pudo vaciar el carrito.";

      Swal.fire({
        icon: "error",
        title: "Error",
        text: msg,
      });
    }
  };

  const total = carritoItems.reduce(
    (acc, item) => acc + (item.precio_unitario || 0) * (item.cantidad || 0),
    0
  );

  const cantidadTotal = carritoItems.reduce(
    (acc, item) => acc + (item.cantidad || 0),
    0
  );

  return (
    <CarritoContext.Provider
      value={{
        carrito,
        carritoItems,
        loadingCarrito,
        cargarCarrito,
        agregarAlCarrito,
        actualizarCantidad,
        eliminarDelCarrito,
        vaciarCarrito,
        total,
        cantidadTotal,
      }}
    >
      {children}
    </CarritoContext.Provider>
  );
};

export const useCarrito = () => useContext(CarritoContext);

export default CarritoContext;